import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  loadPricingConfig,
  WINDOW_BULK_TIERS,
  MESH_OPTIONS,
  bulkDiscountFor,
  nextBulkTier,
  whiteGloveFee,
  meshById,
} from "../lib/pricingConfig";

const STORAGE_KEY = "sz_order_state_v5";  // bump: added discountCode + discountInfo

const initialState = {
  stepId: "product",
  product: null,
  quantity: 1,
  petscreenAll: false,             // sliding doors only
  meshType: "standard",            // windows only ("standard" | "ultraview" | "no_see_ums" | "solar_80")
  whiteGlove: false,               // pre-checkout decision (window+qty>=3 only)
  customer: {
    name: "", email: "", phone: "",
    address_line1: "", address_line2: "",
    city: "", state: "", zip_code: "",
    sms_consent: false,
  },
  upsells: { pro_measurement: false, delivery: false, delivery_install: false, pickup: true, white_glove: false },
  items: [],
  orderId: null,
  orderNumber: null,
  measurementToken: null,
  measurementsRequired: true,
  // Admin-managed promo code applied at the Summary step. `discountInfo`
  // holds the most-recent server-validated preview ({percent, description})
  // so the UI doesn't have to re-fetch on every render.
  discountCode: "",
  discountInfo: null,
};

// Re-export pricing helpers so existing imports `from "../context/OrderContext"`
// keep working unchanged. The authoritative source is now /api/pricing-config
// (fetched once at app load by the OrderProvider below).
export { WINDOW_BULK_TIERS, MESH_OPTIONS, bulkDiscountFor, nextBulkTier, whiteGloveFee, meshById };

const OrderContext = createContext(null);

const FRAME_COLORS_DATA = [
  { name: "White" },
  { name: "Bronze" }, { name: "Black" }, { name: "Tan" },
  { name: "Almond" },
];
export const FRAME_COLORS = FRAME_COLORS_DATA;

/** Build the dynamic step list. Windows get mesh; sliding doors get petscreen. */
function buildStepIds(product, quantity) {
  const isWindow = product?.type === "window";
  const wgEligible = isWindow && (quantity || 0) >= 3;
  return [
    "product",
    "quantity",
    isWindow ? "mesh" : "petscreen",
    ...(wgEligible ? ["whiteGlove"] : []),
    "summary",
    "checkout",
    "upsell",
    "measurements",
    "done",
  ];
}

export function OrderProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...initialState, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return initialState;
  });

  // Fetch the authoritative pricing config once so tiers/mesh options never
  // drift between backend and frontend. Fallback values cover first-paint.
  const [configLoaded, setConfigLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    loadPricingConfig().finally(() => { if (alive) setConfigLoaded(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const whiteGloveEligible = state.product?.type === "window" && (state.quantity || 0) >= 3;

  const stepIds = useMemo(
    () => buildStepIds(state.product, state.quantity),
    [state.product, state.quantity],
  );

  const update = useCallback((patch) => setState((s) => ({ ...s, ...patch })), []);
  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState(initialState);
  }, []);

  const goTo = useCallback((id) => setState((s) => ({ ...s, stepId: id })), []);

  const goNext = useCallback(() => {
    setState((s) => {
      const list = buildStepIds(s.product, s.quantity);
      const idx = list.indexOf(s.stepId);
      const nextIdx = Math.min(idx + 1, list.length - 1);
      return { ...s, stepId: list[nextIdx] };
    });
  }, []);

  const goBack = useCallback(() => {
    setState((s) => {
      const list = buildStepIds(s.product, s.quantity);
      const idx = list.indexOf(s.stepId);
      const prevIdx = Math.max(idx - 1, 0);
      return { ...s, stepId: list[prevIdx] };
    });
  }, []);

  const stepIndex = stepIds.indexOf(state.stepId);
  const stepNumber = stepIndex < 0 ? 1 : stepIndex + 1;
  const stepCount = stepIds.length;

  return (
    <OrderContext.Provider
      value={{ state, update, reset, goTo, goNext, goBack, stepIds, stepNumber, stepCount, whiteGloveEligible, configLoaded }}
    >
      {children}
    </OrderContext.Provider>
  );
}

export function useOrder() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrder must be used within OrderProvider");
  return ctx;
}
